/**
 * PDF pages as images with pdf.js, loaded only when a preview opens. Not the browser's own viewer: the console's CSP
 * (`object-src 'none'`, no `blob:` frames) keeps it out, and phones show only the first page of a framed PDF.
 */
export interface PdfPages {
  /** PNG data URLs of the first `maxPages` pages. */
  pages: string[];
  total: number;
}

export async function renderPdf(bytes: Uint8Array, maxPages: number, width = 1200): Promise<PdfPages> {
  const pdfjs = await import('pdfjs-dist');
  const { default: workerSrc } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
  // pdf.js takes ownership of the buffer: a copy keeps the caller's bytes for the download
  const doc = await pdfjs.getDocument({ data: bytes.slice(), isEvalSupported: false }).promise;
  try {
    const pages: string[] = [];
    for (let n = 1; n <= Math.min(doc.numPages, maxPages); n++) {
      const page = await doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: width / base.width });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvas, viewport }).promise;
      pages.push(canvas.toDataURL('image/png'));
      page.cleanup();
    }
    return { pages, total: doc.numPages };
  } finally {
    await doc.destroy();
  }
}
